import {
  resolveSectorFormPreset as resolvePreset,
  suggestEmptyFacilityFields as suggestFields,
} from '@carbon-intelligence/shared/msmeSectorFormPresets.js';

export type SectorFormPreset = {
  id: string;
  label: string;
  aliases: string[];
  unitNames: string[];
  products: string[];
  processes: string[];
  machinery: string[];
  fuels: string[];
  energySources: string[];
  materials: string[];
  capacityUnits: string[];
  chemicals: string[];
  waterTreatments: string[];
};

export const resolveSectorFormPreset = (input: {
  businessDomain?: unknown;
  industry?: unknown;
  industrySector?: unknown;
} | null | undefined): SectorFormPreset => resolvePreset(input);

export const suggestEmptyFacilityFields = <T extends {
  unitName: string;
  products: string;
  primaryEnergySource: string;
  mainFuelsUsed: string;
  productionCapacityUnit: string;
}>(preset: SectorFormPreset, current: T): T => suggestFields(preset, current);

export const MACHINE_FUEL_OPTIONS = ['electricity', 'diesel', 'petrol', 'cng', 'lpg', 'coal', 'custom'] as const;

export type MachineFuelOption = (typeof MACHINE_FUEL_OPTIONS)[number];

export const machineFuelType = (fuel: string): MachineFuelOption => {
  const normalized = fuel.toLowerCase();
  if ((MACHINE_FUEL_OPTIONS as readonly string[]).includes(normalized) && normalized !== 'custom') {
    return normalized as MachineFuelOption;
  }
  return 'custom';
};

export const energyNeedsDiscom = (energy: string) => /electric|grid/i.test(energy);

const asList = (value: unknown, fallback: string[]) => (
  Array.isArray(value) && value.length > 0 ? value.map((item) => String(item)) : fallback
);

/** Keep a local preset when the operations-template agent omits a list. */
export const coerceSectorFormPreset = (
  plant: Partial<SectorFormPreset> | null | undefined,
  fallback: SectorFormPreset,
): SectorFormPreset => {
  if (!plant || !Array.isArray(plant.processes) || plant.processes.length === 0 || !plant.label) {
    return fallback;
  }
  return {
    id: String(plant.id || fallback.id),
    label: String(plant.label),
    aliases: fallback.aliases,
    unitNames: asList(plant.unitNames, fallback.unitNames),
    products: asList(plant.products, fallback.products),
    processes: asList(plant.processes, fallback.processes),
    machinery: asList(plant.machinery, fallback.machinery),
    fuels: asList(plant.fuels, fallback.fuels),
    energySources: asList(plant.energySources, fallback.energySources),
    materials: asList(plant.materials, fallback.materials),
    capacityUnits: asList(plant.capacityUnits, fallback.capacityUnits),
    chemicals: asList(plant.chemicals, fallback.chemicals),
    waterTreatments: asList(plant.waterTreatments, fallback.waterTreatments),
  };
};
